<template>
    <UModal
        :open="shellOpen"
        :overlay="false"
        :fullscreen="true"
        :dismissible="false"
        :close="false"
        :transition="false"
        :ui="{
            content:
                'loading-scrim-modal-content inset-0 max-w-none max-h-none w-full h-full translate-x-0 translate-y-0 top-0 left-0 rounded-none shadow-none ring-0 divide-none !bg-transparent',
        }"
        @update:open="emit('update:open', $event)"
        @after-leave="emit('after:leave')"
    >
        <template #content>
            <div
                class="loading-scrim-root relative flex min-h-0 min-h-full flex-col text-(--primary-700)"
                :style="{ zIndex: props.zIndex }"
            >
                <div
                    class="loading-scrim-backdrop pointer-events-auto absolute inset-0 ![background:var(--express-app-blue-gradient)]"
                    aria-hidden="true"
                />

                <Transition name="loading-scrim-body" @after-leave="onBodyAfterLeave">
                    <div
                        v-show="effectiveOpen"
                        class="loading-scrim-body pointer-events-auto relative flex min-h-0 flex-1 flex-col pb-[max(3rem,env(safe-area-inset-bottom))]"
                        role="status"
                        aria-live="polite"
                        aria-busy="true"
                        :aria-label="props.ariaLabel"
                    >
                        <div
                            class="loading-scrim-hero flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-2 pt-6"
                        >
                            <slot name="hero">
                                <img
                                    v-if="props.heroImageSrc"
                                    :src="props.heroImageSrc"
                                    :alt="props.heroAlt"
                                    class="max-h-[min(42vh,280px)] w-auto max-w-[min(100%,280px)] object-contain object-center"
                                />
                            </slot>
                        </div>

                        <div
                            class="loading-scrim-card mx-4 mt-4 mb-5 shrink-0 rounded bg-(--ui-bg-white) px-4 py-5 shadow-sm"
                        >
                            <section class="border-b border-(--neutral-200) pb-4">
                                <SharedEyebrow class="mb-3 text-(--neutral-500)">
                                    {{ props.statusEyebrow }}
                                </SharedEyebrow>
                                <div class="flex items-start justify-between gap-3">
                                    <div class="min-w-0 flex-1">
                                        <slot name="status-title">
                                            <p
                                                class="font-serif text-3xl leading-[120%] text-(--primary-700) md:text-4xl"
                                            >
                                                {{ props.statusTitlePrefix
                                                }}<span class="italic">{{
                                                    props.statusTitleEmphasis
                                                }}</span
                                                >{{ props.statusTitleSuffix }}
                                            </p>
                                        </slot>
                                    </div>
                                    <SharedDashedCircleSpinner root-class="shrink-0" />
                                </div>
                                <SharedPulseLoadingBar />
                            </section>

                            <section
                                v-if="showRecipientBlock"
                                class="border-b border-(--neutral-200) py-4"
                            >
                                <LabeledDetailStack
                                    :label="props.recipientEyebrow"
                                    :name="props.recipientName"
                                    :address="props.recipientAddress"
                                />
                            </section>

                            <section v-if="showReturnBlock" class="py-4">
                                <LabeledDetailStack
                                    :label="props.returnEyebrow"
                                    :name="props.returnName"
                                    :address="props.returnAddress"
                                />
                            </section>
                        </div>
                    </div>
                </Transition>
            </div>
        </template>
    </UModal>
</template>

<script setup lang="ts">
/**
 * Full-screen Processing-style loading scrim via `UModal` (`overlay={false}`).
 * Modal shell fades in first; hero/body and card slide vertically (nested Vue transition).
 * On close, body slides out before the shell fades (shellOpen lags props.open).
 *
 * App-root checkout submit uses {@link HandwritingSubmitLoadingScrimHost} (same component, TK-12945).
 *
 * Bottom inset uses `env(safe-area-inset-bottom)` (Capacitor / iOS home indicator) on the body,
 * with `mb-5` on the card so the white panel sits slightly above that padding.
 */
import LabeledDetailStack from './shared/LabeledDetailStack.vue'

const props = withDefaults(
    defineProps<{
        /** Bound by Nuxt `OverlayProvider` (`v-model:open`) or app-root host state. */
        open?: boolean
        heroImageSrc?: string
        heroAlt?: string
        statusEyebrow?: string
        statusTitlePrefix?: string
        statusTitleEmphasis?: string
        statusTitleSuffix?: string
        recipientEyebrow?: string
        recipientName?: string
        recipientAddress?: string
        returnEyebrow?: string
        returnName?: string
        returnAddress?: string
        zIndex?: number
        ariaLabel?: string
    }>(),
    {
        open: false,
        heroImageSrc: '/images/processing-order-hero.svg',
        heroAlt: 'Illustration of pen writing on stationery',
        statusEyebrow: 'STATUS',
        statusTitlePrefix: 'Processing ',
        statusTitleEmphasis: 'order',
        statusTitleSuffix: '...',
        recipientEyebrow: 'RECIPIENT',
        recipientName: '',
        recipientAddress: '',
        returnEyebrow: 'RETURN ADDRESS',
        returnName: '',
        returnAddress: '',
        zIndex: 1000,
        ariaLabel: 'Processing order',
    }
)

const emit = defineEmits<{
    /**
     * Nuxt `OverlayProvider` forwards `@close` for every overlay. Declared so Vue does not treat it
     * as a DOM fallthrough listener. This scrim does not emit it.
     */
    close: [value?: unknown]
    /** `v-model:open` from `OverlayProvider`. */
    'update:open': [value: boolean]
    /** Teardown after modal leave animation. */
    'after:leave': []
}>()

const shellOpen = ref(false)
const effectiveOpen = ref(false)
let bodyEnterTimer: ReturnType<typeof setTimeout> | null = null

function clearBodyEnterTimer(): void {
    if (bodyEnterTimer) {
        clearTimeout(bodyEnterTimer)
        bodyEnterTimer = null
    }
}

/** After hero/card slide-out, close the modal shell (fade-out) so leave motion is visible. */
function onBodyAfterLeave(): void {
    if (!props.open) {
        shellOpen.value = false
    }
}

/**
 * `shellOpen` drives UModal; `effectiveOpen` drives the hero/card slide. On close, body leaves
 * first, then the shell fades out (app-root host stays mounted across routes).
 */
watch(
    () => props.open,
    (newVal) => {
        clearBodyEnterTimer()
        if (newVal) {
            shellOpen.value = true
            bodyEnterTimer = setTimeout(() => {
                effectiveOpen.value = true
                bodyEnterTimer = null
            }, 100)
        } else {
            const hadVisibleBody = effectiveOpen.value
            effectiveOpen.value = false
            if (!hadVisibleBody) {
                shellOpen.value = false
            }
        }
    },
    { immediate: true }
)

onBeforeUnmount(() => {
    clearBodyEnterTimer()
})

const showRecipientBlock = computed(
    () => Boolean(props.recipientName?.trim()) || Boolean(props.recipientAddress?.trim())
)

const showReturnBlock = computed(
    () => Boolean(props.returnName?.trim()) || Boolean(props.returnAddress?.trim())
)
</script>

<style scoped>
.loading-scrim-body-enter-active,
.loading-scrim-body-leave-active,
.loading-scrim-card {
    transition:
        opacity 300ms ease,
        transform 300ms ease;
}

.loading-scrim-body-leave-active {
    transition-delay: 200ms;
}

.loading-scrim-body-enter-from,
.loading-scrim-body-leave-to {
    opacity: 0;
    transform: translateY(-2rem);
}

.loading-scrim-body-enter-active .loading-scrim-card {
    transition-delay: 100ms;
}

.loading-scrim-body-enter-to .loading-scrim-card,
.loading-scrim-body-leave-from .loading-scrim-card {
    opacity: 1;
    transform: translateY(0);
}

.loading-scrim-body-enter-from .loading-scrim-card,
.loading-scrim-body-leave-to .loading-scrim-card {
    opacity: 0;
    transform: translateY(-2rem);
}

@media (prefers-reduced-motion: reduce) {
    .loading-scrim-body-enter-active,
    .loading-scrim-body-leave-active {
        transition: none;
    }

    .loading-scrim-body-enter-from,
    .loading-scrim-body-leave-to {
        opacity: 1;
    }
}
</style>

<style>
/**
 * Opacity-only enter/leave on the modal shell (`:transition="false"` skips Nuxt scale-in/out).
 * Reka `Presence` still waits on `animationend` for `after:leave`.
 */
.loading-scrim-modal-content[data-state='open'] {
    animation: loading-scrim-modal-fade-in 200ms ease-out forwards;
}

.loading-scrim-modal-content[data-state='closed'] {
    animation: loading-scrim-modal-fade-out 200ms ease-in 400ms forwards;
}

@keyframes loading-scrim-modal-fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes loading-scrim-modal-fade-out {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .loading-scrim-modal-content[data-state='open'],
    .loading-scrim-modal-content[data-state='closed'] {
        animation: none;
        opacity: 1;
    }
}
</style>
